import apiClient from '@/lib/api';

/**
 * Émission d'événements du tunnel de réservation — issue #69 / S2-08.
 *
 * <p>Chaque entrée d'étape, abandon et confirmation est transmise au backend, qui
 * incrémente des compteurs Prometheus agrégés. Sans cela, on ne sait pas où les patients
 * abandonnent : le tunnel a 8 étapes, et c'est la première question à poser.
 *
 * <p><b>Aucune donnée personnelle n'est transmise.</b> L'événement ne porte que :
 * <ul>
 *   <li>le type ({@code STEP_ENTERED}, {@code ABANDONED}, {@code COMPLETED}) ;</li>
 *   <li>l'étape ({@code CALENDAR}, {@code SLOTS}, ...) ;</li>
 *   <li>une cause d'abandon, issue d'une liste fermée.</li>
 * </ul>
 * Pas d'identifiant de patient, pas de contenu de formulaire, pas d'adresse IP. Un service
 * de santé qui journaliserait « le patient X a abandonné à l'étape QUESTIONS » créerait un
 * traitement de données de santé sans base légale.
 *
 * <p>L'envoi est <b>best-effort</b> : un échec de télémétrie ne doit jamais bloquer ni
 * ralentir une réservation. Les erreurs sont avalées volontairement.
 */

export type BookingStepName =
  | 'SEARCH'
  | 'RESULTS'
  | 'DETAILS'
  | 'CALENDAR'
  | 'SLOTS'
  | 'QUESTIONS'
  | 'SUMMARY'
  | 'CONFIRMATION';

export type AbandonReason =
  | 'NO_AVAILABILITY'
  | 'API_ERROR'
  | 'AUTH_REQUIRED'
  | 'PRICE'
  | 'SLOT_TAKEN'
  | 'BACK_NAVIGATION'
  | 'SESSION_TIMEOUT'
  | 'UNKNOWN';

type BookingEvent =
  | { event: 'STEP_ENTERED'; step: BookingStepName }
  | { event: 'STEP_LEFT'; step: BookingStepName }
  | { event: 'ABANDONED'; step: BookingStepName; reason: AbandonReason }
  | { event: 'COMPLETED' };

/**
 * Transmet un événement. Ne lève jamais : la télémétrie ne doit pas casser le parcours.
 */
export function trackBookingEvent(payload: BookingEvent): void {
  // `keepalive` permet à la requête d'aboutir même si la page est en cours de fermeture
  // (abandon par fermeture d'onglet, le cas le plus fréquent).
  void apiClient
    .post('/observability/booking-events', payload, { timeout: 3000 })
    .catch(() => {
      // Silencieux par conception : une métrique perdue vaut mieux qu'un tunnel bloqué.
    });
}

/** Raccourci pour les entrées d'étape. */
export function trackStepEntered(step: BookingStepName): void {
  trackBookingEvent({ event: 'STEP_ENTERED', step });
}

/** Raccourci pour les abandons. */
export function trackAbandon(step: BookingStepName, reason: AbandonReason): void {
  trackBookingEvent({ event: 'ABANDONED', step, reason });
}
